import { useQuery } from '@tanstack/react-query'
import { roleAtLeast } from '@lms/shared-types'
import type { Role } from '@lms/shared-types'
import { apiRequestPaged } from '../api/client'
import { listHrReviewsPage } from '../api/hr-leave-reviews'
import type { NavCounts } from '../components/shell/nav-config'
import { flags } from './feature-flags'
import { useSession } from '../auth/auth-context'

/**
 * Live pending-action counts for the sidebar badge system.
 *
 * Polls every 60 s to give managers / directors a glanceable sense of their
 * action queue without hammering the API. Counts only appear once loaded —
 * the badge is simply absent while queries are in-flight or if the role
 * doesn't have access.
 *
 * Data sources:
 *   - manager-requests : GET /api/v1/leaves?status=pending&scope=team&limit=1
 *                        Uses `meta.total` from the paged envelope.
 *                        TEMPORARY: limit=1 is used because the backend computes
 *                        `meta.total` as `len(items)` (not a DB COUNT), so this
 *                        returns at most 1 item. A dedicated count endpoint would
 *                        give the real total. For now this shows "1" (or 0) as a
 *                        presence indicator until the backend adds a true count.
 *                        TODO: ask backend to expose GET /api/v1/leaves/pending-count
 *                        so we get an exact number without fetching the full page.
 *   - For director+, the escalated-to-them queue (GET /api/v1/leaves?
 *     status=escalated&scope=all) folds INTO the same manager-requests badge,
 *     because escalations now live in the Approvals "For approvals" tab. The
 *     badge therefore matches that tab's row count (pending + escalated-to-me).
 *
 * Roles gated:
 *   - manager-requests : manager+ (pending), plus escalated-to-me for director+
 */

export type { NavCounts }

const POLL_INTERVAL_MS = 60_000

export function useNavCounts(role: Role): NavCounts {
  const isManager = roleAtLeast(role, 'manager')
  const isDirector = roleAtLeast(role, 'director')
  // HR-first review queue folds into the Approvals badge for HR ops (HR +
  // admin/superadmin, NOT line directors/exec) so a waiting document review
  // calls for action from the sidebar (feature-gated). Matches the HR-only
  // "HR Doc Review" tab visibility on the Approvals page.
  const showHrReview =
    flags.hrDocumentReview && (role === 'hr' || roleAtLeast(role, 'admin'))

  // The badge mirrors the visible queue, which hides the viewer's own
  // leaves (a manager can't approve themselves).
  const { user } = useSession()
  const effectiveEmployeeId = user?.id ?? null

  const pendingRequestsQuery = useQuery({
    queryKey: ['nav-counts', 'manager-requests', role],
    queryFn: async () => {
      // Pull the page and filter out the viewer's own rows so the badge
      // matches what /manager/requests actually displays. limit=200 is
      // the API max; pending queues larger than that just show "200" today.
      const result = await apiRequestPaged<{ employee: { id: string } }>(
        '/api/v1/leaves?status=pending&scope=team&limit=200',
      )
      if (!effectiveEmployeeId) return result.meta.total
      return result.data.filter((l) => l.employee.id !== effectiveEmployeeId)
        .length
    },
    enabled: isManager,
    refetchInterval: POLL_INTERVAL_MS,
    staleTime: POLL_INTERVAL_MS,
    // Don't block first paint — silently succeed or fail in the background
    retry: false,
  })

  // Escalation terminates at the Exec Director, so director+ also have an
  // escalated-to-them queue that now lives in the Approvals "For approvals"
  // tab. Poll the same ESCALATED queue and FOLD it into the manager-requests
  // badge below so the badge equals that tab's row count.
  const directorDecisionsQuery = useQuery({
    queryKey: ['nav-counts', 'director-decisions', role],
    queryFn: async () => {
      const result = await apiRequestPaged<unknown>(
        '/api/v1/leaves?status=escalated&scope=all&limit=200',
      )
      return result.meta.total
    },
    enabled: isDirector,
    refetchInterval: POLL_INTERVAL_MS,
    staleTime: POLL_INTERVAL_MS,
    retry: false,
  })

  // HR review queue count — exact DB total via the paged envelope.
  const hrReviewQuery = useQuery({
    queryKey: ['nav-counts', 'hr-doc-review', role],
    queryFn: () => listHrReviewsPage({ page: 1, limit: 1 }).then((r) => r.meta.total),
    enabled: showHrReview,
    refetchInterval: POLL_INTERVAL_MS,
    staleTime: POLL_INTERVAL_MS,
    retry: false,
  })

  // WFH approvals routed to this manager. Its own badge (separate from leave),
  // gated on the feature flag + the same manager+ floor as the For Approvals tab.
  const wfhApprovalsQuery = useQuery({
    queryKey: ['nav-counts', 'wfh-approvals', role],
    queryFn: () =>
      apiRequestPaged<unknown>('/api/v1/wfh/pending?page=1&limit=1').then((r) => r.meta.total),
    enabled: isManager && flags.wfhRequest,
    refetchInterval: POLL_INTERVAL_MS,
    staleTime: POLL_INTERVAL_MS,
    retry: false,
  })

  const counts: NavCounts = {}

  const hrReviewCount = showHrReview ? hrReviewQuery.data ?? 0 : 0
  if (isManager && pendingRequestsQuery.data != null) {
    // For director+, add the escalated-to-them count so the single Approvals
    // badge reflects everything in the "For approvals" tab (pending +
    // escalated). Managers have no escalated-to-me queue, so it's pending only.
    // For HR, the pending_hr_review queue folds in too, so the badge surfaces a
    // waiting document review even when the for-approvals queue is empty.
    const escalatedToMe = isDirector ? directorDecisionsQuery.data ?? 0 : 0
    counts['manager-requests'] = pendingRequestsQuery.data + escalatedToMe + hrReviewCount
  } else if (hrReviewCount > 0) {
    // HR with no team-pending rows still needs the badge for the review queue.
    counts['manager-requests'] = hrReviewCount
  }

  if (isManager && flags.wfhRequest && wfhApprovalsQuery.data != null) {
    counts['wfh-approvals'] = wfhApprovalsQuery.data
  }

  return counts
}
